// Per-Block-instance card rendered inside the Template editor's ordered
// list. Per spec/build-plan.md P-031 row + tech-arch.md §7.
//
// **Mounted `<Form>` per instance, keyed by `instanceId`.** Each card
// hosts one `<Form>` over the Block's `optionsSchema`; the `idPrefix`
// uniqueifies HTML ids when the same Block appears twice in the
// Template (e.g. S7 Signal's two `event-card-group` instances at
// Big-3 + Signature slots). The Preact-side `key={instanceId}` on the
// parent's map keeps form state from being reused across reorders.
//
// **`def: BlockDef` = structural slice of `BlockDefinition`.** The full
// `BlockDefinition<any, any>` carries an `any`-generic surface that the
// registry uses (D-080 / blocks.ts comment); this card consumes only
// the fields it needs.
//
// **Defaults section (P-037.31 / D-139).** When `showDefaults` is true,
// a second `<Form>` mounts beneath the options form, walking the same
// `inputsSchema` the Email editor uses for content fields. The
// Template-level value pre-fills into Emails at `createEmailFromTemplate`
// time per Tech Arch §2.5. The form's initial display value is the
// Template-instance `defaultInputs` if set, falling back to the
// BlockDefinition's `defaultInputs` (registry seed) so staff sees what
// the Email author would see by default. Edits dispatch the full form
// shape through `onDefaultInputsChange` — staff who don't edit leave
// `instance.defaultInputs` undefined, and the merge layer in
// `createEmailFromTemplate` falls through to the registry default.

import type { z } from 'zod'

import type { TemplateBlockInstance } from '../../store/schemas'
import { secondaryButtonClassIconish } from '../button-classes'
import { Form } from '../form/form'

interface BlockDef {
  readonly id: string
  readonly label: string
  readonly optionsSchema: z.ZodTypeAny
  readonly defaultOptions: unknown
  readonly inputsSchema: z.ZodTypeAny
  readonly defaultInputs: unknown
}

interface BlockInstanceCardProps {
  readonly instance: TemplateBlockInstance
  readonly def: BlockDef
  readonly canMoveUp: boolean
  readonly canMoveDown: boolean
  readonly onMoveUp: () => void
  readonly onMoveDown: () => void
  readonly onRemove: () => void
  readonly onOptionsChange: (next: unknown) => void
  // P-037.31 / D-139 — Defaults section is rendered only when the Template
  // editor opts the Block in (the V1 whitelist of 3 in `template-editor.tsx`).
  readonly showDefaults?: boolean
  readonly onDefaultInputsChange?: (next: unknown) => void
}

export function BlockInstanceCard({
  instance,
  def,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
  onOptionsChange,
  showDefaults = false,
  onDefaultInputsChange,
}: BlockInstanceCardProps) {
  return (
    <li class="rounded border border-gray-200 bg-white">
      <header class="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2">
        <div class="min-w-0">
          <div class="truncate text-sm font-medium text-gray-900">{def.label}</div>
          <code class="text-xs text-gray-500">{def.id}</code>
        </div>
        <div class="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            aria-label="Move block up"
            class={secondaryButtonClassIconish}
          >
            ↑
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            aria-label="Move block down"
            class={secondaryButtonClassIconish}
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove block"
            class="rounded border border-red-300 bg-white px-2 py-1 text-sm text-red-700 hover:bg-red-50"
          >
            🗑
          </button>
        </div>
      </header>
      <div class="px-3 py-3">
        <Form
          schema={def.optionsSchema}
          value={instance.options ?? def.defaultOptions}
          onChange={onOptionsChange}
          idPrefix={`block-${instance.instanceId}`}
          blockId={def.id}
        />
      </div>
      {showDefaults && onDefaultInputsChange !== undefined && (
        <div class="border-t border-gray-100 px-3 py-3">
          <h3 class="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Defaults
          </h3>
          <Form
            schema={def.inputsSchema}
            value={instance.defaultInputs ?? def.defaultInputs}
            onChange={onDefaultInputsChange}
            idPrefix={`block-${instance.instanceId}-defaults`}
            blockId={def.id}
          />
        </div>
      )}
    </li>
  )
}
